import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { CartItem } from './components/CartItem';
import { CodeField } from './components/CodeField';
import { SummaryCard } from './components/SummaryCard';
import { EmptyCart } from './components/EmptyCart';
import { SavedForLater } from './components/SavedForLater';
import { CheckoutModal } from './components/CheckoutModal';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { fetchReleaseDetail } from '../../lib/releasesApi';
import { mapReleaseDetail } from '../../lib/releaseMappers';
import { ApiError, API_BASE_URL, apiRequest } from '../../lib/api';
import { syncCartWithServer as syncCartWithServerApi } from '../../lib/cartSync';
import { trackPreviewPlay } from '../../lib/analyticsApi';
import { useAuth } from '../../hooks/useAuth';
import { AuthPromptModal } from './components/AuthPromptModal';
import type { MpesaCheckoutResult, MpesaStatusDetails } from './components/CheckoutModal';

type PricedCartItem = {
  cart_item_id: number;
  release_id: number;
  track_id: number | null;
  item_type: 'release' | 'track' | 'tip';
  unit_price_cents: number;
  discount_cents: number;
  quantity: number;
  net_cents: number;
  release_slug?: string | null;
};

type PricedCart = {
  cart_id: number;
  items: PricedCartItem[];
  subtotal_cents: number;
  discount_total_cents: number;
  tip_cents: number;
  total_cents: number;
  promo_code?: string | null;
  promo_error?: string | null;
};

type PurchaseCartItem = import('../../context/CartContext').CartItem & {
  kind: 'release' | 'track';
};
type TipCartItem = import('../../context/CartContext').CartItem & {
  kind: 'tip';
  tipForItemId?: string;
  tipSource?: 'artist' | 'cart-item';
};

export const CartPage = () => {
  const {
    items: cartItems,
    savedItems,
    updateItemQuantity,
    removeItem,
    saveItemForLater,
    moveSavedToCart,
    removeSavedItem,
    addItem,
    clearCart,
  } = useCart();
  const [appliedCode, setAppliedCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [serverCart, setServerCart] = useState<PricedCart | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isApplyingCode, setIsApplyingCode] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [checkoutMethod, setCheckoutMethod] = useState<'mpesa' | 'card'>('mpesa');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPreviewKey, setCurrentPreviewKey] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [loadingPreviewSlug, setLoadingPreviewSlug] = useState<string | null>(null);
  const [previewTrackMap, setPreviewTrackMap] = useState<Record<string, number>>({});
  const [isAuthPromptOpen, setIsAuthPromptOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, token } = useAuth();

  const tipItems = useMemo<TipCartItem[]>(
    () => cartItems.filter((item): item is TipCartItem => item.kind === 'tip'),
    [cartItems]
  );
  const purchaseItems = useMemo<PurchaseCartItem[]>(
    () => cartItems.filter((item): item is PurchaseCartItem => item.kind !== 'tip'),
    [cartItems]
  );
  const itemTipsByCartId = useMemo<Record<string, TipCartItem>>(() => {
    const map: Record<string, TipCartItem> = {};
    tipItems.forEach((tip) => {
      if (tip.tipForItemId) {
        map[tip.tipForItemId] = tip;
      }
    });
    return map;
  }, [tipItems]);
  const artistTips = useMemo(
    () => tipItems.filter((tip) => !tip.tipForItemId),
    [tipItems]
  );

  const tipTotal = useMemo(
    () => tipItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [tipItems]
  );

  const subtotal = useMemo(
    () => purchaseItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [purchaseItems]
  );
  const effectiveSubtotal = serverCart?.subtotal_cents ?? subtotal;
  const effectiveDiscount = serverCart?.discount_total_cents ?? discount;
  const effectiveTip = tipTotal;
  const netBase = Math.max(0, effectiveSubtotal - effectiveDiscount);
  const total = netBase + effectiveTip;

  const handleAddItemTip = useCallback(
    (item: PurchaseCartItem, amount: number) => {
      if (amount <= 0) return;
      const linkedTip = itemTipsByCartId[item.id];
      if (linkedTip) {
        removeItem(linkedTip.id);
      }
      const idBase = item.releaseSlug ?? item.trackId ?? item.releaseId ?? item.id;
      addItem({
        id: `tip-${idBase}`,
        kind: 'tip',
        releaseId: item.releaseId,
        trackId: item.trackId,
        title: `Tip for ${item.title}`,
        artist: item.artist,
        artistSlug: item.artistSlug,
        artistId: item.artistId,
        releaseSlug: item.releaseSlug,
        cover: item.cover,
        price: amount,
        isPWYW: false,
        quantity: 1,
        formats: [],
        extras: [],
        tipForItemId: item.id,
        tipSource: 'cart-item',
      });
    },
    [addItem, itemTipsByCartId, removeItem]
  );

  const handleRemoveItemTip = useCallback(
    (itemId: string) => {
      const linkedTip = itemTipsByCartId[itemId];
      if (linkedTip) {
        removeItem(linkedTip.id);
      }
    },
    [itemTipsByCartId, removeItem]
  );

  const handleRemovePurchaseItem = useCallback(
    (id: string) => {
      handleRemoveItemTip(id);
      removeItem(id);
    },
    [handleRemoveItemTip, removeItem]
  );

  const handleSavePurchaseForLater = useCallback(
    (id: string) => {
      handleRemoveItemTip(id);
      saveItemForLater(id);
    },
    [handleRemoveItemTip, saveItemForLater]
  );

  const handleCodeApply = async (code: string) => {
    setIsApplyingCode(true);
    setCodeError(null);
    try {
      await syncCartWithServer();
      const response = await apiRequest<{ cart: PricedCart }>('/cart/code/apply', {
        method: 'POST',
        authToken: token ?? undefined,
        body: JSON.stringify({ code }),
      });
      setServerCart(response.cart);
      setAppliedCode(response.cart.promo_code ?? code);
      setDiscount(response.cart.discount_total_cents ?? 0);
      setIsCodeValid(true);
      addToast({
        message: 'Discount code applied',
        variant: 'success',
      });
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to apply this code. Please try again.';
      setIsCodeValid(false);
      setServerCart(null);
      setCodeError(message);
      addToast({ message, variant: 'error' });
    } finally {
      setIsApplyingCode(false);
    }
  };

  const handleCodeRemove = async () => {
    setIsApplyingCode(true);
    try {
      await syncCartWithServer();
      const response = await apiRequest<{ cart: PricedCart }>('/cart/code/remove', {
        method: 'POST',
        authToken: token ?? undefined,
      });
      setServerCart(response.cart);
      setAppliedCode('');
      setDiscount(0);
      setIsCodeValid(false);
      setCodeError(null);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to remove the code right now.';
      addToast({ message, variant: 'error' });
    } finally {
      setIsApplyingCode(false);
    }
  };

  const syncCartWithServer = useCallback(
    async (options?: { reapplyPromo?: string | null }) => {
      const cartId = await syncCartWithServerApi({
        items: cartItems,
        token,
      });
      const promo = options?.reapplyPromo?.trim();
      if (promo) {
        const response = await apiRequest<{ cart: PricedCart }>('/cart/code/apply', {
          method: 'POST',
          authToken: token ?? undefined,
          body: JSON.stringify({ code: promo }),
        });
        setServerCart(response.cart);
        setAppliedCode(response.cart.promo_code ?? promo);
        setDiscount(response.cart.discount_total_cents ?? 0);
        setIsCodeValid(true);
        setCodeError(null);
      }
      return cartId;
    },
    [cartItems, token]
  );

  const handleCheckoutSuccess = useCallback(() => {
    clearCart();
    setAppliedCode('');
    setDiscount(0);
    setServerCart(null);
    setCodeError(null);
    setIsCodeValid(false);
    setOrderNotes('');
    setIsCheckoutModalOpen(false);
    navigate('/library');
  }, [clearCart, navigate]);

  const handleFreeCheckout = useCallback(async () => {
    if (!token) {
      setIsAuthPromptOpen(true);
      return;
    }
    setIsLoading(true);
    try {
      const cartId = await syncCartWithServer({
        reapplyPromo: appliedCode || serverCart?.promo_code || null,
      });
      const response = await apiRequest<{
        ok: boolean;
        order_id: number;
        number: string;
        payment_id: number;
      }>('/orders/checkout/free', {
        method: 'POST',
        authToken: token,
        body: JSON.stringify({ cart_id: cartId }),
      });
      addToast({
        message: `Checkout complete. Order #${response.number} is ready in your Library.`,
        variant: 'success',
      });
      handleCheckoutSuccess();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to complete checkout. Please try again.';
      addToast({ message, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    syncCartWithServer,
    addToast,
    setIsAuthPromptOpen,
    handleCheckoutSuccess,
    appliedCode,
    serverCart,
  ]);

  const handleCheckout = (method: 'mpesa' | 'card' | 'free') => {
    if (method === 'card') {
      addToast({
        message: 'Card Payments are Coming Soon, Please use MPesa',
      });
      return;
    }
    if (!user) {
      setIsAuthPromptOpen(true);
      return;
    }

    if (method === 'free') {
      void handleFreeCheckout();
      return;
    }

    setCheckoutMethod(method);
    setIsLoading(false);
    setIsCheckoutModalOpen(true);
  };


  const handlePreview = async ({ releaseSlug, trackId }: { releaseSlug?: string; trackId?: number }) => {
    if (!releaseSlug && !trackId) {
      addToast({
        message: 'Preview unavailable for this item.',
        variant: 'error',
      });
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;

    const previewKey = trackId ? `track-${trackId}` : releaseSlug ?? null;
    if (!previewKey) {
      addToast({ message: 'Preview unavailable for this item.', variant: 'error' });
      return;
    }

    if (currentPreviewKey === previewKey) {
      if (isPreviewPlaying) {
        audio.pause();
        setIsPreviewPlaying(false);
      } else {
        audio
          .play()
          .then(() => setIsPreviewPlaying(true))
          .catch(() => {
            setIsPreviewPlaying(false);
            addToast({ message: 'Unable to resume preview.', variant: 'error' });
          });
      }
      return;
    }

    let releaseId: number | undefined;
    let previewTrackId = trackId;

    if (trackId) {
      const associatedRelease = cartItems.find(
        (item) => item.trackId === trackId || item.releaseSlug === releaseSlug
      );
      releaseId = associatedRelease?.releaseId;
    } else if (releaseSlug) {
      const cartItem = cartItems.find((item) => item.releaseSlug === releaseSlug);
      releaseId = cartItem?.releaseId;
    }

    if (!previewTrackId && releaseSlug) {
      setLoadingPreviewSlug(releaseSlug);
      try {
        const detailResponse = await fetchReleaseDetail(releaseSlug);
        const detail = mapReleaseDetail(detailResponse);
        const previewTrack = detail.tracks.find((track) => track.hasPreview);
        if (!previewTrack) {
          addToast({
            message: 'No preview available for this release yet.',
            variant: 'error',
          });
          setLoadingPreviewSlug(null);
          return;
        }
        previewTrackId = previewTrack.id;
        setPreviewTrackMap((prev) => ({ ...prev, [releaseSlug]: previewTrack.id }));
      } catch (error) {
        console.error('Failed to load release preview', error);
        addToast({
          message: 'Unable to load preview right now.',
          variant: 'error',
        });
        setLoadingPreviewSlug(null);
        return;
      }
      setLoadingPreviewSlug(null);
    } else if (!previewTrackId) {
      if (releaseSlug) {
        setLoadingPreviewSlug(releaseSlug);
      }
      addToast({ message: 'Preview unavailable for this track.', variant: 'error' });
      if (releaseSlug) {
        setLoadingPreviewSlug(null);
      }
      return;
    }

    const previewUrl = `${API_BASE_URL}/previews/${previewTrackId}.mp3`;
    audio.pause();
    audio.src = previewUrl;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => {
        setCurrentPreviewKey(previewKey);
        setIsPreviewPlaying(true);
        void trackPreviewPlay({
          trackId: previewTrackId!,
          releaseId,
          source: 'cart_page',
        });
      })
      .catch(() => {
        setIsPreviewPlaying(false);
        addToast({
          message: 'Unable to play preview.',
          variant: 'error',
        });
      });
  };

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audioRef.current = audio;

    const handleEnded = () => {
      setIsPreviewPlaying(false);
      setCurrentPreviewKey(null);
    };
    const handleError = () => {
      setIsPreviewPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    document.title = 'Cart — 254Tracks';
  }, []);

  useEffect(() => {
    if (cartItems.length === 0) {
      setServerCart(null);
      setAppliedCode('');
      setDiscount(0);
      setIsCodeValid(false);
      setCodeError(null);
    }
  }, [cartItems.length]);

  const initiateMpesaCheckout = useCallback(
    async (phone: string): Promise<MpesaCheckoutResult> => {
      if (!token) {
        throw new Error('Please sign in to checkout.');
      }
      setIsLoading(true);
      try {
        const cartId = await syncCartWithServer({
          reapplyPromo: appliedCode || serverCart?.promo_code || null,
        });
        const response = await apiRequest<{
          ok: boolean;
          order_id: number;
          number: string;
          payment_id: number;
          checkout_request_id: string;
          merchant_request_id: string;
          customer_message?: string | null;
        }>('/orders/checkout/mpesa', {
          method: 'POST',
          authToken: token,
          body: JSON.stringify({ cart_id: cartId, phone }),
        });
        const result: MpesaCheckoutResult = {
          orderId: response.order_id,
          orderNumber: response.number,
          paymentId: response.payment_id,
          checkoutRequestId: response.checkout_request_id,
          merchantRequestId: response.merchant_request_id,
          customerMessage: response.customer_message ?? null,
          phone,
        };
        addToast({
          message: result.customerMessage ?? 'STK push sent. Check your phone to approve the payment.',
          variant: 'success',
        });
        return result;
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : 'Unable to initiate MPESA checkout. Please try again.';
        addToast({ message, variant: 'error' });
        throw error instanceof Error ? error : new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [token, syncCartWithServer, addToast, appliedCode, serverCart]
  );

  const pollMpesaStatus = useCallback(
    async (checkoutRequestId: string, options?: { refresh?: boolean }): Promise<MpesaStatusDetails> => {
      if (!token) {
        throw new Error('Please sign in to checkout.');
      }
      const query = options?.refresh ? '?refresh=1' : '';
      const response = await apiRequest<{
        checkout_request_id: string;
        order_id: number;
        payment_id: number;
        status: MpesaStatusDetails['status'];
        order_status: string | null;
        payment_status: string | null;
        result_code: number | null;
        result_desc: string | null;
        mpesa_receipt: string | null;
        transaction_date: string | null;
        amount_cents: number;
        currency: string;
        phone: string;
        updated_at: string;
        refresh_error?: string | null;
      }>(`/orders/checkout/mpesa/${checkoutRequestId}${query}`, {
        authToken: token,
      });
      return {
        checkoutRequestId: response.checkout_request_id,
        orderId: response.order_id,
        paymentId: response.payment_id,
        status: response.status,
        orderStatus: response.order_status,
        paymentStatus: response.payment_status,
        resultCode: response.result_code,
        resultDescription: response.result_desc,
        mpesaReceipt: response.mpesa_receipt,
        amountCents: response.amount_cents,
        currency: response.currency,
        phone: response.phone,
        transactionDate: response.transaction_date,
        updatedAt: response.updated_at,
        refreshError: response.refresh_error ?? null,
      };
    },
    [token]
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />

      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Your Cart</h1>
            {cartItems.length > 0 && (
              <p className="text-gray-600 mt-2">
                {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your cart
              </p>
            )}
          </div>

          {cartItems.length === 0 ? (
            <EmptyCart />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  {purchaseItems.map((item) => {
                    const previewKey =
                      item.kind === 'track' && item.trackId
                        ? `track-${item.trackId}`
                        : item.releaseSlug ?? null;
                    const isPreviewActive =
                      previewKey !== null && currentPreviewKey === previewKey && isPreviewPlaying;
                    const isLoadingPreview =
                      item.kind === 'release' && loadingPreviewSlug === item.releaseSlug;
                    return (
                      <CartItem
                        key={item.id}
                        {...item}
                        tipAmount={itemTipsByCartId[item.id]?.price}
                        isPreviewPlaying={isPreviewActive}
                        isPreviewLoading={isLoadingPreview}
                        onUpdateQuantity={updateItemQuantity}
                        onAddTip={(amount) => handleAddItemTip(item, amount)}
                        onRemoveTip={() => handleRemoveItemTip(item.id)}
                        onRemove={handleRemovePurchaseItem}
                        onSaveForLater={handleSavePurchaseForLater}
                        onPreview={(options) =>
                          handlePreview({
                            releaseSlug: options.releaseSlug ?? item.releaseSlug,
                            trackId: options.trackId ?? item.previewTrackId ?? item.trackId,
                          })
                        }
                      />
                    );
                  })}
                </div>

                {artistTips.length > 0 && (
                  <div className="bg-white border border-emerald-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Artist tips</h3>
                    <div className="space-y-3">
                      {artistTips.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between text-sm text-gray-800"
                        >
                          <div className="flex items-center gap-2">
                            <i className="ri-hand-heart-line text-emerald-600" />
                            <div>
                              <p className="font-semibold">{item.title}</p>
                              <p className="text-gray-500">{item.artist}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-emerald-700">
                              KES {(item.price * item.quantity).toLocaleString()}
                            </span>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <CodeField
                  onCodeApply={handleCodeApply}
                  onCodeRemove={handleCodeRemove}
                  appliedCode={serverCart?.promo_code ?? appliedCode}
                  discount={serverCart?.discount_total_cents ?? discount}
                  isValid={serverCart ? !serverCart.promo_error : isCodeValid}
                  errorMessage={codeError ?? serverCart?.promo_error ?? null}
                  isLoading={isApplyingCode}
                />

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label htmlFor="order-notes" className="block font-medium text-gray-900 mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    id="order-notes"
                    rows={3}
                    placeholder="Gift message to artist or special instructions..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {orderNotes.length}/500 characters
                  </div>
                </div>

                <SavedForLater
                  items={savedItems}
                  onMoveToCart={moveSavedToCart}
                  onRemove={removeSavedItem}
                />
              </div>

              <div className="lg:col-span-1">
                <SummaryCard
                  subtotal={effectiveSubtotal}
                  discount={effectiveDiscount}
                  tip={effectiveTip}
                  total={total}
                  onCheckout={handleCheckout}
                  isLoading={isLoading}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        onSuccess={handleCheckoutSuccess}
        method={checkoutMethod}
        isLoading={isLoading}
        onMpesaCheckout={initiateMpesaCheckout}
        pollMpesaStatus={pollMpesaStatus}
        total={total}
      />
      <AuthPromptModal isOpen={isAuthPromptOpen} onClose={() => setIsAuthPromptOpen(false)} />
    </div>
  );
};
